import * as React from "react";
import { fetchCities, ICity } from "@nju/data-access-portal-wwt";
import { Combobox } from "@nju/form";
import { Loader } from "@nju/ui";

export function CityAutocomplete({
  postCode,
  onChange,
  onNotFound,
  state,
  defaultValue,
  isDisabled,
}: {
  postCode?: string;
  selectedItem?: ICity | null;
  onChange: (item?: ICity) => void;
  onNotFound?: (error?: string) => void;
  state?: "error";
  defaultValue?: string;
  isDisabled?: boolean;
}) {
  const [inputValue, setInputValue] = React.useState("");
  const [data, setData] = React.useState<ICity[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  async function validateCity({
    postCode,
    query,
  }: {
    postCode: string;
    query: string;
  }) {
    setIsLoading(true);
    try {
      const results = (await fetchCities({ query, postCode })).unwrap();
      if (results.length === 0) {
        onNotFound?.(
          "Miejscowość nie istnieje lub nie jest powiązana z podanym kodem pocztowym.",
        );
      }
    } finally {
      setIsLoading(false);
    }
  }

  React.useEffect(() => {
    async function action(signal: AbortSignal) {
      if (inputValue.length < 3) {
        return;
      }
      try {
        setIsLoading(true);
        const list = (
          await fetchCities({ query: inputValue, signal })
        ).unwrap();
        setData(list);
      } catch {
        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
    }

    const abortController = new AbortController();
    action(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [inputValue]);

  return (
    <Combobox
      defaultValue={
        defaultValue ? [{ name: defaultValue, id: 1 } as ICity] : undefined
      }
      inputDisplayValue={([value]) => {
        return value?.name || "";
      }}
      isDisabled={isDisabled}
      itemComparator={(first, second) => {
        return first.id === second.id;
      }}
      items={data}
      key={defaultValue}
      label="miejscowość"
      listColorVariant="gray"
      listItemRenderer={({ item }) => {
        return (
          <div>
            {item.name}
            <br />
            <span>
              woj. {item.stateOrProvince}, pow. {item.district}, gm.{" "}
              {item.community}
            </span>
          </div>
        );
      }}
      onInputChange={setInputValue}
      onSelect={([element]) => {
        if (element) {
          postCode && validateCity({ postCode, query: element.name });
          onChange(element);
        }
      }}
      openOnFocus={false}
      state={state}
      suffix={isLoading ? <Loader /> : undefined}
      uniqueItemId={(item) => {
        return item.id.toString();
      }}
    />
  );
}
